import { Injectable, inject } from '@angular/core';
import { EnumTranslator } from '@skylab-angular-shared/core';
import { CommunityTransactionForSaleQuery, VatRateForSaleBaseQuery } from '@skylab-shared/business';
import { BusinessObjectBase, LocalContext, ModelBase, ObservableCollection, RepositoryBase } from '@skylab-shared/core';
import { CalculationMethod, DocumentItemModel, DocumentVatIssueMode, EnumTranslatorBase, LabeledEnumValue, LabeledEnumValueConverter, SaleDocumentKind, SplitPaymentState } from '@skylab-shared/dataModel';
import { FormBase, FormBaseViewModel } from '../form/form-base';

export type DocumentFormViewModelVatIssueModeExtension = {
    vatIssueModes: LabeledEnumValue[];
    vatIssueModesConverter: LabeledEnumValueConverter<DocumentVatIssueMode>;
    vatIssueModeVisible: boolean;
    vatIssueMode: {
        value: DocumentVatIssueMode;
    }
};

interface DocumentModel extends ModelBase {
    issuedByVatPayer: boolean;
    kind: SaleDocumentKind;
    communityTransactionId: number;
    calculationMethod: CalculationMethod;
    taxLiabilityDate: Date | null;
    splitPayment: SplitPaymentState;
    items: ObservableCollection<DocumentItemModel>;
}

@Injectable({ providedIn: 'root' })
export class DocumentFormViewModelVatIssueModeExtensionService {
    private enumTranslator = inject(EnumTranslator) as EnumTranslatorBase;

    public extendViewModel<TViewModelPart, TViewModel extends TViewModelPart & FormBaseViewModel, TBO extends BusinessObjectBase<TModel>, TModel extends ModelBase & DocumentModel>(
        viewModel: TViewModelPart,
        formComponent: FormBase<TViewModel, RepositoryBase<TBO, TModel>, TBO, TModel>,
        localContext: LocalContext,
        visibleCondition: () => boolean,
        onChanged: () => void): TViewModelPart & DocumentFormViewModelVatIssueModeExtension {
        return Object.assign(viewModel as any, this.getViewModelExtension(formComponent, localContext, visibleCondition, onChanged));
    }

    private getViewModelExtension<TViewModel extends FormBaseViewModel, TBO extends BusinessObjectBase<TModel>, TModel extends ModelBase & DocumentModel>(
        formComponent: FormBase<TViewModel, RepositoryBase<TBO, TModel>, TBO, TModel>,
        localContext: LocalContext,
        visibleCondition: () => boolean,
        onChanged: () => void): DocumentFormViewModelVatIssueModeExtension {
        const model = formComponent.bo.model;

        const vatIssueModeVisible = visibleCondition();
        const vatIssueModes = this.enumTranslator.getLabeledEnumValueArray(DocumentVatIssueMode);
        const vatIssueModesConverter = new LabeledEnumValueConverter<DocumentVatIssueMode>(this.enumTranslator, DocumentVatIssueMode);

        const viewModelExtension: DocumentFormViewModelVatIssueModeExtension = {
            vatIssueModeVisible,
            vatIssueModes,
            vatIssueModesConverter,
            vatIssueMode: {
                get value(): DocumentVatIssueMode {
                    return model.issuedByVatPayer ? DocumentVatIssueMode.VatPayer : DocumentVatIssueMode.NonVatPayer;
                },
                set value(value: DocumentVatIssueMode) {
                    model.issuedByVatPayer = value === DocumentVatIssueMode.VatPayer;

                    if (!model.issuedByVatPayer) {
                        const vatRates = localContext.getPreloaded(VatRateForSaleBaseQuery);
                        const { id: defaultCommunityTransactionId } = localContext.firstPreloaded(CommunityTransactionForSaleQuery, { default: true });

                        model.communityTransactionId = defaultCommunityTransactionId;
                        model.calculationMethod = CalculationMethod.Gross;
                        model.taxLiabilityDate = null;
                        model.splitPayment = SplitPaymentState.False;
                        model.items.forEach(item => { 
                            item.vatRateId = vatRates.find(vatRate => vatRate.exempt)?.id!;
                        });
                    }

                    onChanged();
                }
            },
        };

        return viewModelExtension;
    }
}
